/**
 * V6.4 verse-anchor — the tappable label above each pericope commentary
 * paragraph. Tapping navigates the Bible reader to the range's first verse,
 * same mechanism as InlineVerseRef (setCurrentLocation → scrollToVerseElement
 * + highlight).
 *
 * Two presentations:
 *  - Scripture form (preferred): the range's actual KJV text as the link
 *    text, verse numbers at the end — the printed-commentary format. Used
 *    whenever the passage's verse texts are available (live pericope study).
 *  - Pill fallback: the bare "Verses 4-6" label — recalled history entries
 *    and any context where verse texts are absent.
 * Both degrade to a static (non-tappable) element when the passage context
 * can't resolve to a location.
 */

import { parseReference } from '@skjv/common';
import { bibleState } from '@/state/bible-state';

export interface AnchorContext {
  /** Book name as it appears in references (e.g. "Revelation"). */
  book: string;
  /** 1-based chapter, as in pericope data and reference strings. */
  chapter: number;
  /** Passage verse texts (1-based verse numbers), when the call site has
   *  them — enables the scripture-form anchor. */
  verses?: Array<{ verse: number; text: string }>;
}

interface CommentaryAnchorChipProps {
  displayText: string;
  startVerse: number;
  context: AnchorContext | null;
  /** KJV text of the anchor's range; null → pill fallback. */
  scripture?: string | null;
}

export function CommentaryAnchorChip({
  displayText,
  startVerse,
  context,
  scripture,
}: CommentaryAnchorChipProps) {
  const location = context
    ? parseReference(`${context.book} ${context.chapter}:${startVerse}`)
    : null;

  if (scripture) {
    const body = (
      <>
        <span className="commentary-anchor-scripture-text">{scripture}</span>
        <span className="commentary-anchor-scripture-range"> — {displayText}</span>
      </>
    );
    return location ? (
      <button
        className="commentary-anchor-scripture"
        onClick={() => bibleState.setCurrentLocation(location)}
        title={`Go to ${context!.book} ${context!.chapter}:${startVerse}`}
      >
        {body}
      </button>
    ) : (
      <span className="commentary-anchor-scripture">{body}</span>
    );
  }

  if (!location) {
    return <span className="commentary-anchor-chip">{displayText}</span>;
  }

  return (
    <button
      className="commentary-anchor-chip"
      onClick={() => bibleState.setCurrentLocation(location)}
      title={`Go to ${context!.book} ${context!.chapter}:${startVerse}`}
    >
      {displayText}
    </button>
  );
}
